import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, Gavel, FileDown, Play, Volume2, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MicButton } from '../voice/VoiceControls';
import { pollJob, downloadBlob } from '../../utils/aiJobs';
import { GuideView } from './GuideView';

const clean = (t) => (t || '').replace(/\*\*|__|^#+\s*/gm, '');

export const ProceduralBriefing = ({ session, setSession, voiceOn, speak, onBegin }) => {
  const { api } = useAuth();
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const [starting, setStarting] = useState(false);
  const [loadingGuide, setLoadingGuide] = useState(!session.guide);
  const spoken = useRef(false);

  const waitGuide = async (jobId) => {
    setLoadingGuide(true);
    try {
      if (jobId) await pollJob(api, jobId, { interval: 4000 });
      else {
        for (let i = 0; i < 60; i += 1) {
          const { data } = await api.get(`/crm/rehearsals/${session.id}`);
          if (data.guide) break;
          await new Promise((r) => setTimeout(r, 4000));
        }
      }
      const { data } = await api.get(`/crm/rehearsals/${session.id}`);
      setSession(data);
    } catch (err) {
      toast.error(err.message || 'El juez no pudo preparar la explicación');
    } finally {
      setLoadingGuide(false);
    }
  };

  useEffect(() => { if (!session.guide) waitGuide(session.guide_job_id); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (session.guide && voiceOn && !spoken.current) {
      spoken.current = true;
      speak(session.guide.explicacion_oral, true);
    }
  }, [session.guide, voiceOn, speak]);

  const retry = async () => {
    const { data } = await api.post(`/crm/rehearsals/${session.id}/guide`);
    waitGuide(data.job_id);
  };

  const ask = async (e, spokenText) => {
    e?.preventDefault();
    const text = (spokenText ?? q).trim();
    if (text.length < 2 || asking) return;
    setQ('');
    setAsking(true);
    try {
      const { data } = await api.post(`/crm/rehearsals/${session.id}/briefing/ask`, { text });
      setSession((s) => ({ ...s, briefing_qa: [...(s.briefing_qa || []), { q: text, a: data.answer }] }));
      if (voiceOn) speak(data.answer, true);
    } catch (err) {
      setQ(text);
      toast.error(err.response?.data?.detail || 'El juez no respondió');
    } finally {
      setAsking(false);
    }
  };

  const pdf = async () => {
    try {
      const r = await api.get(`/crm/rehearsals/${session.id}/guide/pdf`, { responseType: 'blob' });
      downloadBlob(r.data, 'guia_procesal.pdf');
    } catch { toast.error('No se pudo generar el PDF'); }
  };

  const begin = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/crm/rehearsals/${session.id}/begin`);
      onBegin(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo iniciar el juicio');
    } finally {
      setStarting(false);
    }
  };

  if (loadingGuide) {
    return (
      <div data-testid="briefing-loading" className="flex flex-col items-center justify-center gap-3 py-12 text-center text-sm text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
        El juez está preparando la explicación del procedimiento (1-2 minutos)…
      </div>
    );
  }
  if (!session.guide) {
    return (
      <div className="py-10 text-center">
        <button data-testid="briefing-retry-btn" onClick={retry} className="inline-flex items-center gap-2 rounded-full border border-amber-500/60 px-4 py-2 text-sm text-amber-200"><RefreshCw className="h-4 w-4" /> Preparar explicación de nuevo</button>
      </div>
    );
  }

  const g = session.guide;
  return (
    <div data-testid="procedural-briefing" className="space-y-4">
      <div className="rounded-2xl rounded-bl-md bg-slate-800 px-4 py-3 text-sm text-slate-100">
        <div className="mb-1 flex items-center justify-between">
          <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400"><Gavel className="h-3 w-3" /> Juez · Explicación del procedimiento</p>
          <button data-testid="briefing-listen-btn" onClick={() => speak(g.explicacion_oral)} className="text-slate-400 hover:text-amber-300" title="Escuchar / pausar"><Volume2 className="h-4 w-4" /></button>
        </div>
        <p data-testid="briefing-oral" className="whitespace-pre-line">{g.explicacion_oral}</p>
      </div>
      <GuideView g={g} />
      <div className="space-y-2" data-testid="briefing-qa">
        {(session.briefing_qa || []).map((x, i) => (
          <div key={i} className="space-y-2">
            <div className="flex justify-end"><p className="max-w-[85%] rounded-2xl rounded-br-md bg-amber-500 px-4 py-2 text-sm text-slate-950">{x.q}</p></div>
            <div data-testid={`briefing-answer-${i}`} className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-bl-md bg-slate-800 px-4 py-2 text-sm text-slate-100">{clean(x.a)}</div>
          </div>
        ))}
        {asking && <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" /> El juez está respondiendo…</p>}
      </div>
      <form onSubmit={ask} className="flex gap-2 border-t border-slate-800 pt-3">
        <textarea data-testid="briefing-input" rows={2} value={q} onChange={(e) => setQ(e.target.value)} disabled={asking}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) ask(e); }}
          placeholder="Pregunta al juez sobre plazos, excepciones, recursos, resoluciones…"
          className="flex-1 resize-none rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white" />
        <div className="flex flex-col gap-2">
          <MicButton testid="briefing-mic-btn" disabled={asking} onText={(t) => ask(null, t)} />
          <button data-testid="briefing-send-btn" disabled={asking || q.trim().length < 2} className="rounded-lg bg-amber-500 px-3 py-2 text-slate-950 disabled:opacity-50" aria-label="Preguntar"><Send className="h-4 w-4" /></button>
        </div>
      </form>
      <div className="flex flex-wrap justify-end gap-2">
        <button data-testid="briefing-pdf-btn" onClick={pdf} className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-800"><FileDown className="h-4 w-4" /> Descargar guía PDF</button>
        <button data-testid="briefing-begin-btn" onClick={begin} disabled={starting} className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60">
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Iniciar juicio simulado
        </button>
      </div>
    </div>
  );
};
